import { getFormattedNoteActivityDateString } from '~/lib/util/patient.util';
import { EditorV2State } from '@equip.health/ui';
import type { Survey as SurveyJS } from 'survey-react-ui';
import {
  AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { MutableRefObject, RefObject } from 'react';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { FormikProps } from 'formik';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';

type BuildUpdateNoteRequestFromFormikReqArgs = {
  appointmentExternalId: string | null;
  authoredByProviderExternalId: string;
  formikRef: MutableRefObject<FormikProps<EditNoteFormData>>;
  inquiryId?: string;
  noteId: string;
  noteStatus: NoteStatus;
  uploadedFiles: AttachmentType[];
  noteTemplateRevisionFormRef: RefObject<SurveyJS>;
  noteTemplateRevisionSurveyJsId?: string | null;
  noteContentEditorRef: RefObject<EditorV2State | null>;
  patientId?: string;
};

type BuildUpdateNoteRequestArgs = Omit<
  BuildUpdateNoteRequestFromFormikReqArgs,
  'formikRef' | 'noteTemplateRevisionFormRef'
> & {
  formValues: EditNoteFormData;
  noteTemplateRevisionData: Record<string, unknown> | null;
  noteContentEditorRef: RefObject<EditorV2State | null>;
  patientId?: string;
  sessionSummaryEditorRef?: RefObject<EditorV2State | null>;
};

type BuildUpdateGroupClassNoteRequestFromFormikReqArgs = {
  formikRef: MutableRefObject<FormikProps<EditNoteFormData>>;
  noteId: string | null;
  noteStatus: NoteStatus;
  noteContentEditorRef: RefObject<EditorV2State | null>;
} & (
  | { groupClassEventExternalId: string }
  | { groupSectionEventExternalId: string }
);

export const buildUpdateGroupClassNoteRequestFromFormikRef = (
  {
    formikRef,
    noteStatus,
    noteId,
    noteContentEditorRef,
    ...groupEventIdentifier
  }: BuildUpdateGroupClassNoteRequestFromFormikReqArgs,
  isAddendumModal: boolean = false,
): UpdateGroupClassEventNoteRequest | null => {
  if (!formikRef?.current?.values) {
    return null;
  }
  const { values } = formikRef.current;
  const {
    activity,
    activityDate,
    addendumContent,
    isConfidential,
    medium,
    title,
  } = values;
  const req: UpdateGroupClassEventNoteRequest = {
    activityDate: getFormattedNoteActivityDateString(activityDate),
    activityWith: (activity ?? []).map((activity) => ({
      activityWithName: activity.value,
    })),
    ...groupEventIdentifier,
    isConfidential,
    noteExternalId: noteId,
    noteStatus,
    tags: [],
    title,
  };

  if (isAddendumModal) {
    req.addendumContent = addendumContent;
  }
  req.noteContent = noteContentEditorRef.current?.getHTML();

  if (medium?.value) {
    req.noteCommunicationMethod = medium.value;
  }

  return req;
};

export const buildCreateAddendumRequest = (
  formikRef: MutableRefObject<FormikProps<EditNoteFormData>>,
  appointmentExternalId?: string,
  attachmentPayload?: AddendumAttachmentChanges | undefined,
): AddNoteAddendumRequest => {
  if (!formikRef?.current?.values) {
    return null;
  }

  const { values } = formikRef.current;
  const {
    title,
    type,
    medium,
    isConfidential,
    activityDate,
    appointment,
    addendumContent,
    activity,
  } = values;
  return {
    title,
    noteAddendumContent: addendumContent,
    noteType: type?.value,
    noteCommunicationMethod: medium?.value,
    isSensitive: isConfidential,
    activityDate: getFormattedNoteActivityDateString(activityDate),
    appointmentExternalId: appointment?.node || appointmentExternalId || null,
    activityWith: (activity ?? []).map((activity) => ({
      activityWithName: activity.value,
    })),
    ...attachmentPayload,
  };
};

export const buildUpdateNoteRequest = ({
  appointmentExternalId = null,
  authoredByProviderExternalId,
  formValues,
  inquiryId,
  noteStatus,
  noteId,
  uploadedFiles,
  noteTemplateRevisionData,
  noteTemplateRevisionSurveyJsId,
  noteContentEditorRef,
  sessionSummaryEditorRef,
  patientId,
}: BuildUpdateNoteRequestArgs) => {
  const {
    aiSummaryId,
    activity,
    activityDate,
    appointment,
    isConfidential,
    medium,
    title,
    type,
    noteContentCache,
    noteTemplateRevision,
    sessionSummary,
  } = formValues;
  let noteTemplateRevisionId = noteTemplateRevision?.node || null;
  if (noteTemplateRevisionId === NO_NOTE_TEMPLATE_OPTION.node) {
    noteTemplateRevisionId = null;
  }

  let sessionSummaryContent = sessionSummary;
  if (sessionSummaryEditorRef?.current) {
    sessionSummaryContent = sessionSummaryEditorRef.current.isEmpty
      ? ''
      : sessionSummaryEditorRef.current.getHTML();
  }

  const req: UpdateNoteRequest = {
    aiSummaryId,
    activityDate: getFormattedNoteActivityDateString(activityDate),
    activityWith: (activity ?? []).map((activity) => ({
      activityWithName: activity.value,
    })),
    authoredByProviderExternalId,
    appointmentExternalId: appointment?.node || appointmentExternalId || null,
    attachments: uploadedFiles,
    linkedDocumentExternalIds: uploadedFiles.flatMap((file) =>
      file.attachmentExternalId ? [file.attachmentExternalId] : [],
    ),
    patientExternalId: patientId,
    isConfidential,
    noteExternalId: noteId,
    noteStatus,
    tags: [],
    title,
    noteTemplateRevisionId,
    noteTemplateRevisionData,
    noteTemplateRevisionSurveyJsId,
    sessionSummary: sessionSummaryContent,
  };

  // TODO this is a safeguard against currently outdated BE validation - note content should not be required if there is a note template
  // remove this default assignment of empty paragraph when BE validation is fixed
  req.noteContent =
    noteContentEditorRef.current?.getHTML() || noteContentCache || '<p></p>';

  // Backend doesn't want noteType and noteCommunicationMethod key, when its empty
  if (type?.value) {
    req.noteType = type.value;
  }
  if (medium?.value) {
    req.noteCommunicationMethod = medium.value;
  }
  if (inquiryId) {
    req.inquiryId = inquiryId;
  }
  return req;
};

export const buildUpdateNoteRequestFromRefs = ({
  appointmentExternalId = null,
  authoredByProviderExternalId,
  formikRef,
  inquiryId,
  noteStatus,
  noteId,
  uploadedFiles,
  noteTemplateRevisionFormRef,
  noteContentEditorRef,
  patientId,
}: BuildUpdateNoteRequestFromFormikReqArgs): UpdateNoteRequest | null => {
  if (!formikRef?.current?.values) {
    return null;
  }
  const { values } = formikRef.current;
  const noteTemplateRevisionData =
    noteTemplateRevisionFormRef?.current?.props?.model?.data || null;

  return buildUpdateNoteRequest({
    appointmentExternalId,
    authoredByProviderExternalId,
    formValues: values,
    inquiryId,
    noteStatus,
    noteId,
    uploadedFiles,
    noteContentEditorRef,
    noteTemplateRevisionData,
    noteTemplateRevisionSurveyJsId:
      noteTemplateRevisionData?.noteTemplateRevisionSurveyJsId,
    patientId,
  });
};
